import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { Confirmation } from '@abp/ng.theme.shared';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ActionMenuComponent } from 'src/app/shared/components/action-menu/action-menu.component';
import { ActionMenuItem } from 'src/app/shared/components/action-menu/models/action-menu-item.model';
import { BaseListComponent } from 'src/app/shared/components/base-list/base-list.component';
import { BaseListColumn } from 'src/app/shared/components/base-list/models/base-list-column.model';
import { BaseListConfig } from 'src/app/shared/components/base-list/models/base-list-config.model';
import { ListRequestDto } from 'src/core/models/request/list-request.dto';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { CrudService } from 'src/core/services/crud/crud.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { DeclineReasonDto } from 'src/core/models/decline-reason/decline-reason.dto';
import { BaseListColumnTemplateContext } from 'src/app/shared/components/base-list/models/base-list-column-template-context.model';
import { DeclineReasonItemDto } from 'src/core/models/decline-reason/decline-reason-item.dto';

const MAX_INLINE_QUEUES = 3;

type DeclineReasonTemplateContext = BaseListColumnTemplateContext<DeclineReasonDto>;

@Component({
  selector: 'app-decline-reason-list',
  standalone: true,
  imports: [
    CommonModule,
    LocalizationPipe,
    ActionMenuComponent,
    BaseListComponent,
    TagModule,
    TooltipModule,
  ],
  templateUrl: './decline-reason-list.component.html',
})
export class DeclineReasonListComponent implements AfterViewInit {
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly crudService = inject(CrudService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toasterService = inject(ToasterService);

  @ViewChild('nameTpl') nameTpl!: TemplateRef<DeclineReasonTemplateContext>;
  @ViewChild('queuesTpl') queuesTpl!: TemplateRef<DeclineReasonTemplateContext>;
  @ViewChild('actionsTpl') actionsTpl!: TemplateRef<DeclineReasonTemplateContext>;

  @Input() reloadVersion = 0;

  @Output() edit = new EventEmitter<DeclineReasonDto>();
  @Output() deleted = new EventEmitter<void>();

  reasons: DeclineReasonDto[] = [];
  loading = false;
  listConfig!: BaseListConfig<DeclineReasonDto>;

  ngOnInit(): void {
    this.loadList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reloadVersion'] && !changes['reloadVersion'].firstChange) {
      this.loadList();
    }
  }

  ngAfterViewInit(): void {
    const l = (key: string) => this.localization.instant(key);

    const columns: BaseListColumn<DeclineReasonDto>[] = [
      {
        field: 'name',
        header: l('Settings::WorkEnvironment:DeclineReasons:Name'),
        bodyTemplate: this.nameTpl,
      },
      {
        field: 'queues',
        header: l('Settings::WorkEnvironment:DeclineReasons:Queues'),
        bodyTemplate: this.queuesTpl,
      },
      {
        field: 'id',
        header: l('Settings::WorkEnvironment:DeclineReasons:Actions'),
        width: '120px',
        bodyTemplate: this.actionsTpl,
      },
    ];

    this.listConfig = {
      columns,
      trackByField: 'id',
      emptyMessage: l('Settings::WorkEnvironment:DeclineReasons:EmptyMessage'),
    };

    this.cdr.detectChanges();
  }

  getQueuesInline(queues: DeclineReasonItemDto[]): DeclineReasonItemDto[] {
    return queues?.slice(0, MAX_INLINE_QUEUES) ?? [];
  }

  hasQueueOverflow(queues: DeclineReasonItemDto[]): boolean {
    return (queues?.length ?? 0) > MAX_INLINE_QUEUES;
  }

  getQueueOverflowCount(queues: DeclineReasonItemDto[]): number {
    return (queues?.length ?? 0) - MAX_INLINE_QUEUES;
  }

  getQueuesTooltip(queues: DeclineReasonItemDto[]): string {
    return queues?.slice(MAX_INLINE_QUEUES).map(q => q.name).join('\n') ?? '';
  }

  trackById(_index: number, item: DeclineReasonItemDto): number {
    return item.id;
  }

  getMenuItems(row: DeclineReasonDto): ActionMenuItem[] {
    return [
      {
        label: this.localization.instant('Settings::WorkEnvironment:DeclineReasons:Edit'),
        icon: 'pi pi-pencil',
        action: () => this.edit.emit(row),
      },
      {
        label: this.localization.instant('Settings::WorkEnvironment:DeclineReasons:Delete'),
        icon: 'pi pi-trash',
        severity: 'danger',
        action: () => this.onDeleteClick(row),
      },
    ];
  }

  onDeleteClick(row: DeclineReasonDto): void {
    this.confirmationService
      .warn(
        this.localization.instant(
          'Settings::WorkEnvironment:DeclineReasons:DeleteConfirmMessage',
          row.name,
        ),
        'Settings::WorkEnvironment:DeclineReasons:DeleteConfirmTitle',
        {
          hideCancelBtn: false,
          hideYesBtn: false,
          dismissible: true,
          yesText: 'Settings::WorkEnvironment:DeclineReasons:Delete',
          cancelText: 'AbpUi::Cancel',
        },
      )
      .pipe(
        switchMap(status => {
          if (status === Confirmation.Status.confirm) {
            return this.crudService.delete(DeclineReasonDto, row.id).pipe(map(() => true));
          }

          return of(false);
        }),
      )
      .subscribe({
        next: isDeleted => {
          if (!isDeleted) {
            return;
          }

          this.toasterService.success('::SuccessfullyDeleted');
          this.deleted.emit();
        },
      });
  }

  private loadList(): void {
    this.loading = true;

    const request: ListRequestDto = {
      skipCount: 0,
      maxResultCount: 1000,
      filters: [],
      sorters: [],
    };

    this.crudService.get<DeclineReasonDto>(DeclineReasonDto, request).subscribe({
      next: (response: ListResponseDto<DeclineReasonDto>) => {
        this.reasons = response.items ?? [];
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }
}
